import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { FiAlertTriangle, FiX } from 'react-icons/fi';

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const pendingRef = useRef(null);

  const confirm = useCallback((options) => new Promise((resolve) => {
    pendingRef.current?.resolve(false);
    const request = typeof options === 'string' ? { message: options } : options;
    pendingRef.current = { ...request, resolve };
    setDialog(pendingRef.current);
  }), []);

  const close = useCallback((answer) => {
    pendingRef.current?.resolve(answer);
    pendingRef.current = null;
    setDialog(null);
  }, []);

  useEffect(() => {
    if (!dialog) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close, dialog]);

  useEffect(() => () => pendingRef.current?.resolve(false), []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
          role="presentation"
          onMouseDown={(event) => event.target === event.currentTarget && close(false)}
        >
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-title"
            aria-describedby="confirm-message"
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-50 text-red-600">
                <FiAlertTriangle aria-hidden="true" />
              </span>
              <button type="button" onClick={() => close(false)} aria-label="Close confirmation">
                <FiX />
              </button>
            </div>
            <h2 id="confirm-title" className="mt-3 text-lg font-bold text-black">
              {dialog.title || 'Please confirm'}
            </h2>
            <p id="confirm-message" className="mt-2 text-sm text-black">{dialog.message}</p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => close(false)} className="rounded-lg border px-4 py-2 text-sm font-medium">
                {dialog.cancelLabel || 'Cancel'}
              </button>
              <button type="button" autoFocus onClick={() => close(true)} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700">
                {dialog.confirmLabel || 'Confirm'}
              </button>
            </div>
          </section>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used inside ConfirmProvider');
  return confirm;
};
